import logger from "../utils/logger";

/**
 * Standard API error shape returned by all API functions on failure.
 * Callers can check: if (result?.error) { handle error }
 *
 * @param {Error} ex - The caught exception
 * @param {string} [operation] - Name of the API operation for logging context
 * @param {Object} [context] - Additional context metadata for logging
 * @returns {{ error: true, status_code: number, message: string, originalError: Error }}
 */
export const createApiError = (ex, operation = "API call", context = {}) => {
  const apiData = ex?.response?.data;
  const message =
    (typeof apiData?.data === "object" && apiData?.data?.message) ||
    (typeof apiData?.data === "string" && apiData?.data) ||
    apiData?.message ||
    ex?.message ||
    "An unexpected error occurred";
  const statusCode = ex?.response?.status || apiData?.status_code || 0;

  logger.error(`${operation} failed`, ex, context);

  return {
    error: true,
    status_code: statusCode,
    message,
    originalError: ex,
  };
};

/**
 * Wraps an async API call with standardized error handling.
 * On success: returns the axios response data (unwrapped from { data })
 * On failure: returns { error: true, status_code, message, originalError }
 *
 * @param {Function} fn - Async function that returns an axios response
 * @param {string} operationName - Name for logging/Sentry context
 * @param {Object} [context] - Additional context metadata
 * @returns {Promise<*>} The response data or a standard error object
 */
export const withApiErrorHandling = async (fn, operationName, context = {}) => {
  try {
    const { data } = await fn();
    return data;
  } catch (ex) {
    return createApiError(ex, operationName, context);
  }
};
